import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, EmptyState } from "@/components/Primitives";
import api, { formatErr } from "@/lib/api";
import EntityDialog from "@/components/EntityDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Store, Plus, Search, Pencil, Trash2, MoreHorizontal, ArrowRightLeft, ExternalLink, Loader2,
} from "lucide-react";

const HOST_STATUS = ["Active Host", "Inactive Host"];
const FIELDS = [
  { name: "name", label: "Business Name", required: true },
  { name: "contact_name", label: "Contact Name" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Email" },
  { name: "address", label: "Street Address", full: true },
  { name: "city", label: "City" },
  { name: "postal_code", label: "Postal Code" },
  { name: "territory", label: "Zone / Territory", type: "select", optionsSource: "territories" },
  { name: "zone", label: "Zone", type: "select", optionsSource: "zones" },
  { name: "website", label: "Website" },
  { name: "host_status", label: "Host Status", type: "select", options: HOST_STATUS, required: true },
  { name: "assigned_to", label: "Assigned Salesperson", type: "select", optionsSource: "users" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
];

const COLS = [
  { key: "name", label: "Business Name", bold: true },
  { key: "city", label: "City" },
  { key: "zone", label: "Zone" },
  { key: "contact_name", label: "Contact Name" },
  { key: "phone", label: "Phone" },
  { key: "assigned_to_name", label: "Salesperson", muted: true },
  { key: "host_status", label: "Host Status" },
  { key: "lead_status", label: "Lead Status" },
];

const HostStatusChip = ({ v }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
    (v || "Active Host") === "Active Host" ? "bg-[#9ACC32]/15 text-[#5B880C]" : "bg-slate-100 text-slate-500"}`}>
    {v || "Active Host"}
  </span>
);
const LeadStatusChip = ({ exists }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
    exists ? "bg-[#00C9E0]/15 text-[#0094A8]" : "bg-slate-100 text-slate-400"}`} data-testid="host-lead-status">
    {exists ? "Lead Created" : "Not a Lead"}
  </span>
);

export default function HostBusinesses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.role !== "readonly";
  const canDelete = !["sales_rep", "readonly"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState({ users: [], territories: [], zones: [] });
  const [q, setQ] = useState("");
  const [fCity, setFCity] = useState("__all");
  const [fTerr, setFTerr] = useState("__all");
  const [fUser, setFUser] = useState("__all");
  const [fStatus, setFStatus] = useState("__all");
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);
  const [converting, setConverting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/host-businesses")
      .then((r) => setRows(r.data.items || []))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      api.get("/users").catch(() => ({ data: { items: [] } })),
      api.get("/territories").catch(() => ({ data: { items: [] } })),
      api.get("/zones").catch(() => ({ data: { items: [] } })),
    ]).then(([u, t, z]) => setSources({
      users: (u.data.items || []).map((x) => ({ value: x.id, label: x.name })),
      territories: (t.data.items || []).map((x) => ({ value: x.name, label: x.name })),
      zones: (z.data.items || []).map((x) => ({ value: x.name, label: x.name })),
    }));
  }, []);

  const cities = useMemo(() => [...new Set(rows.map((r) => r.city).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (fCity !== "__all" && r.city !== fCity) return false;
      if (fTerr !== "__all" && r.territory !== fTerr) return false;
      if (fUser !== "__all" && r.assigned_to !== fUser) return false;
      if (fStatus !== "__all" && (r.host_status || "Active Host") !== fStatus) return false;
      if (term) {
        const hay = [r.name, r.contact_name, r.city, r.phone, r.email, r.website].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, q, fCity, fTerr, fUser, fStatus]);

  const doConvert = async (h) => {
    setConverting(h.id);
    try {
      const { data } = await api.post(`/host-businesses/${h.id}/convert-to-lead`);
      toast.success(data.duplicate ? "This host is already a lead — opening it." : "Lead created from host business.");
      navigate(`/leads?open=${data.lead_id}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not convert to lead");
    } finally { setConverting(null); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/host-businesses/${confirm.id}`);
      toast.success("Host business deleted."); setConfirm(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const cell = (c, row) => {
    if (c.key === "host_status") return <HostStatusChip v={row.host_status} />;
    if (c.key === "lead_status") return <LeadStatusChip exists={row.lead_exists} />;
    const v = row[c.key];
    return <span className={c.bold ? "font-semibold text-slate-800" : c.muted ? "text-slate-500" : "text-slate-600"}>{v || "—"}</span>;
  };

  const actions = (row) => (
    <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
      {row.lead_exists ? (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/leads?open=${row.lead_id}`)} data-testid={`host-view-lead-${row.id}`}>
          <ExternalLink className="h-3 w-3" />View Lead
        </Button>
      ) : (
        <Button size="sm" className="h-7 text-xs" disabled={converting === row.id} onClick={() => doConvert(row)} data-testid={`host-convert-${row.id}`}>
          {converting === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />}Convert to Lead
        </Button>
      )}
      {canWrite && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-8 w-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" data-testid={`host-menu-${row.id}`}><MoreHorizontal className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setDialog({ open: true, initial: row })} data-testid={`host-edit-${row.id}`}><Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
            {canDelete && <DropdownMenuItem onClick={() => setConfirm(row)} className="text-[#C42607]" data-testid={`host-delete-${row.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  const emptyBlock = (
    <EmptyState icon={Store} title="No host businesses yet"
      hint="Add coffee shops, clinics, stores and other locations that carry ChitChat Local."
      action={canWrite && <Button size="sm" onClick={() => setDialog({ open: true, initial: { host_status: "Active Host" } })}><Plus className="h-4 w-4" />New Host Business</Button>} />
  );

  return (
    <div data-testid="page-host-businesses">
      <PageHeader title="Host Businesses" subtitle="Locations that physically host and distribute ChitChat Local — track them and convert them into advertising leads.">
        {canWrite && (
          <Button size="sm" onClick={() => setDialog({ open: true, initial: { host_status: "Active Host" } })} data-testid="host-create-btn">
            <Plus className="h-4 w-4" />New Host Business
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search host businesses…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="host-search" />
        </div>
        <Select value={fCity} onValueChange={setFCity}>
          <SelectTrigger className="w-40" data-testid="host-filter-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All Cities</SelectItem>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fTerr} onValueChange={setFTerr}>
          <SelectTrigger className="w-44" data-testid="host-filter-territory"><SelectValue placeholder="Zone / Territory" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All Territories</SelectItem>{sources.territories.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fUser} onValueChange={setFUser}>
          <SelectTrigger className="w-44" data-testid="host-filter-user"><SelectValue placeholder="Salesperson" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All Salespeople</SelectItem>{sources.users.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-36" data-testid="host-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All Statuses</SelectItem>{HOST_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block cc-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="host-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70">
                {COLS.map((c) => <th key={c.key} className="px-4 py-3 text-left cc-overline whitespace-nowrap">{c.label}</th>)}
                <th className="px-4 py-3 text-right cc-overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && [...Array(6)].map((_, i) => (
                <tr key={i} className="border-b border-slate-100">
                  {COLS.map((c) => <td key={c.key} className="px-4 py-3.5"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}
                  <td />
                </tr>
              ))}
              {!loading && filtered.map((row) => (
                <tr key={row.id} className="group border-b border-slate-100 last:border-0 hover:bg-[#00C9E0]/[0.04] transition-colors" data-testid={`host-row-${row.id}`}>
                  {COLS.map((c) => <td key={c.key} className="px-4 py-3.5 whitespace-nowrap">{cell(c, row)}</td>)}
                  <td className="px-4 py-3.5 text-right">{actions(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length === 0 && emptyBlock}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && [...Array(4)].map((_, i) => <div key={i} className="cc-card p-4"><div className="h-5 w-1/2 bg-slate-100 rounded animate-pulse mb-3" /><div className="h-4 w-3/4 bg-slate-100 rounded animate-pulse" /></div>)}
        {!loading && filtered.length === 0 && <div className="cc-card">{emptyBlock}</div>}
        {!loading && filtered.map((row) => (
          <div key={row.id} className="cc-card p-4" data-testid={`host-row-${row.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-slate-800 truncate">{row.name}</div>
                <div className="text-xs text-slate-400 mt-0.5 truncate">{[row.city, row.zone].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              <HostStatusChip v={row.host_status} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Contact</p><div className="truncate">{row.contact_name || "—"}</div></div>
              <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Phone</p><div className="truncate">{row.phone || "—"}</div></div>
              <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Salesperson</p><div className="truncate text-slate-500">{row.assigned_to_name || "—"}</div></div>
              <div><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Lead</p><div><LeadStatusChip exists={row.lead_exists} /></div></div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
              {row.lead_exists ? (
                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => navigate(`/leads?open=${row.lead_id}`)} data-testid={`host-view-lead-${row.id}`}><ExternalLink className="h-3 w-3" />View Lead</Button>
              ) : (
                <Button size="sm" className="h-8 text-xs flex-1" disabled={converting === row.id} onClick={() => doConvert(row)} data-testid={`host-convert-${row.id}`}>{converting === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />}Convert to Lead</Button>
              )}
              {canWrite && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setDialog({ open: true, initial: row })} data-testid={`host-edit-${row.id}`}><Pencil className="h-3 w-3" /></Button>}
              {canDelete && <Button size="sm" variant="outline" className="h-8 text-xs border-[#C42607]/40 text-[#C42607]" onClick={() => setConfirm(row)} data-testid={`host-delete-${row.id}`}><Trash2 className="h-3 w-3" /></Button>}
            </div>
          </div>
        ))}
      </div>

      <EntityDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        fields={FIELDS}
        initial={dialog.initial}
        title={dialog.initial?.id ? "Edit Host Business" : "New Host Business"}
        endpoint={dialog.initial?.id ? `/host-businesses/${dialog.initial.id}` : "/host-businesses"}
        method={dialog.initial?.id ? "put" : "post"}
        optionSources={sources}
        onSaved={load}
      />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this host business?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm ? <span className="font-semibold text-slate-700">{confirm.name}<br /></span> : null}
              This host location will be permanently removed. Any lead already created from it is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#C42607] hover:bg-[#9e1e06]" data-testid="host-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
